import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { RefreshCw, Plus, Trash2, Star } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ConfirmDelete } from "@/pages/admin/ConfirmDelete";

export default function AdminTestimonials() {
  const [items, setItems] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ author: "", text: "" });

  const load = () => api.get("/admin/testimonials").then(setItems).catch(() => toast.error("Couldn't load testimonials."));
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/testimonials/sync");
      toast.success(res.added ? `Pulled ${res.added} new testimonial${res.added === 1 ? "" : "s"} from Topmate.` : `Topmate checked — ${res.found} found, nothing new.`);
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSyncing(false);
    }
  };

  const toggle = async (t) => {
    try {
      await api.put(`/testimonials/${t.id}`, { visible: !t.visible });
      setItems(items.map((i) => (i.id === t.id ? { ...i, visible: !t.visible } : i)));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.del(`/testimonials/${id}`);
      toast.success("Testimonial removed.");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/testimonials", { author: form.author.trim(), text: form.text.trim(), rating: 5, visible: true });
      toast.success("Testimonial added.");
      setForm({ author: "", text: "" });
      setAdding(false);
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="max-w-xl text-base text-stone-600">
          Toggle which quotes show on the home page. New Topmate reviews can be pulled in automatically.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAdding(!adding)} data-testid="admin-add-testimonial-button" className="rounded-full gap-2">
            <Plus className="h-4 w-4" /> Add manually
          </Button>
          <Button onClick={sync} disabled={syncing} data-testid="admin-sync-topmate-button" className="rounded-full bg-amber-600 font-semibold text-white hover:bg-amber-700 gap-2">
            <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing..." : "Sync from Topmate"}
          </Button>
        </div>
      </div>

      {adding && (
        <form onSubmit={add} className="mt-6 space-y-4 rounded-3xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="testimonial-form">
          <div className="space-y-1.5">
            <Label htmlFor="t-author">Name</Label>
            <Input id="t-author" required value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="testimonial-author-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="t-text">Quote</Label>
            <Textarea id="t-text" required rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="testimonial-text-input" />
          </div>
          <Button type="submit" data-testid="testimonial-save-button" className="rounded-full bg-amber-600 font-semibold text-white hover:bg-amber-700">Save testimonial</Button>
        </form>
      )}

      <div className="mt-6 space-y-4">
        {items?.length === 0 && <p className="text-stone-500" data-testid="admin-testimonials-empty">No testimonials yet.</p>}
        {items?.map((t) => (
          <div key={t.id} className={`rounded-3xl border bg-white p-6 shadow-sm ${t.visible ? "border-stone-200" : "border-dashed border-stone-300 opacity-70"}`} data-testid={`admin-testimonial-card-${t.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-stone-900">{t.author}</p>
                  <span className="inline-flex items-center gap-0.5 text-amber-500">
                    {Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="h-3 w-3 fill-current" />)}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${t.source === "topmate" ? "bg-indigo-50 text-indigo-700" : "bg-stone-100 text-stone-500"}`}>
                    {t.source}
                  </span>
                  <span className="font-mono text-xs text-stone-400">{format(new Date(t.date), "MMM d, yyyy")}</span>
                </div>
                <p className="mt-3 text-sm text-stone-600 leading-relaxed">{t.text}</p>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs font-semibold text-stone-500">
                  <Switch checked={t.visible} onCheckedChange={() => toggle(t)} data-testid={`admin-testimonial-toggle-${t.id}`} />
                  {t.visible ? "Shown" : "Hidden"}
                </label>
                <ConfirmDelete title={`Remove ${t.author}'s testimonial?`} onConfirm={() => remove(t.id)} triggerTestId={`admin-testimonial-delete-${t.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </ConfirmDelete>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
